import React from 'react';
import toast from 'react-hot-toast';
import { isNativeApp } from './platform';

/**
 * Reliable device-location permission handling for the Anna app's native shell.
 *
 * The WebView's own geolocation prompt caches its "denied" answer for the page
 * session and stops re-prompting, and a permanently-denied Android permission
 * makes ActivityCompat.requestPermissions a silent no-op. Driving it through the
 * native AppSettings plugin sidesteps both, and openAppSettings gives the Anna a
 * way back once Android refuses to show the dialog again.
 *
 * This is the same flow the passenger app ships. Every call that crosses into
 * native code is wrapped in a hard timeout so a missed callback can never hang
 * the chain forever.
 */

class NativeCallTimeoutError extends Error {}

const STATUS_CALL_TIMEOUT_MS = 4000;
const INTERACTIVE_CALL_TIMEOUT_MS = 15000;

const withTimeout = (promise, ms) => Promise.race([
  Promise.resolve(promise),
  new Promise((_, reject) => {
    setTimeout(() => reject(new NativeCallTimeoutError('native_call_timeout')), ms);
  }),
]);

// IMPORTANT: a Capacitor plugin object (from registerPlugin(...)) answers a
// property lookup for *anything* — including "then" — with a callable that
// forwards to native. Handing that object back as a promise's fulfillment value
// makes the engine treat it as a thenable and call plugin.then(...), which
// Capacitor tries to run as a native method "then" and fails silently. So the
// plugin object is kept in a plain box; callers await a loader that resolves to
// undefined, then read the box with ordinary property access.
let appSettingsBox = { plugin: undefined }; // undefined = not loaded, null = load failed
let appSettingsLoading = null;
const ensureAppSettingsLoaded = () => {
  if (appSettingsBox.plugin !== undefined) return Promise.resolve();
  if (!appSettingsLoading) {
    appSettingsLoading = withTimeout(import('@capacitor/core'), STATUS_CALL_TIMEOUT_MS)
      .then(({ registerPlugin }) => { appSettingsBox.plugin = registerPlugin('AppSettings') || null; })
      .catch(() => { appSettingsBox.plugin = null; });
  }
  return appSettingsLoading;
};
const readAppSettings = () => appSettingsBox.plugin || null;

/** Open this app's OS settings screen so the Anna can grant location manually. */
export const openAppSettings = async () => {
  try {
    await ensureAppSettingsLoaded();
    const plugin = readAppSettings();
    await plugin?.openAppSettings?.();
  } catch {
    /* nothing else we can do from the web layer */
  }
};

/** Query native permission + GPS hardware status. */
export const checkNativeLocationStatus = async () => {
  if (!isNativeApp()) return { permission: 'granted', locationServicesEnabled: true };
  await ensureAppSettingsLoaded();
  const plugin = readAppSettings();
  if (!plugin?.checkLocationStatus) return { permission: 'granted', locationServicesEnabled: true };
  try {
    const res = await withTimeout(plugin.checkLocationStatus(), STATUS_CALL_TIMEOUT_MS);
    return {
      permission: res?.permission || 'prompt',
      locationServicesEnabled: !!res?.locationServicesEnabled,
    };
  } catch {
    // Unknown — assume "prompt" so the caller still tries, and assume GPS is on
    // so a real fix attempt fails cleanly on its own rather than stalling here.
    return { permission: 'prompt', locationServicesEnabled: true };
  }
};

/** Google Play Services one-tap: "To continue, turn on device location [OK]". */
export const promptEnableDeviceLocation = async () => {
  if (!isNativeApp()) return true;
  await ensureAppSettingsLoaded();
  const plugin = readAppSettings();
  if (!plugin?.promptEnableLocation) return true;
  try {
    const res = await withTimeout(plugin.promptEnableLocation(), INTERACTIVE_CALL_TIMEOUT_MS);
    return !!res?.enabled;
  } catch {
    return true;
  }
};

/** Natively request fine + coarse location permission. */
export const requestNativeLocationPermission = async () => {
  if (!isNativeApp()) return 'granted';
  await ensureAppSettingsLoaded();
  const plugin = readAppSettings();
  if (!plugin?.requestLocationPermission) return 'granted';
  try {
    const res = await withTimeout(plugin.requestLocationPermission(), INTERACTIVE_CALL_TIMEOUT_MS);
    if (res?.granted) return 'granted';
    return res?.permission || 'denied';
  } catch (e) {
    // A timeout may mean the system dialog is still open and unanswered. Don't
    // fire a second competing request — "denied" surfaces the Settings prompt,
    // and a real grant still takes effect next time this runs.
    return 'denied';
  }
};

/**
 * Returns 'granted' | 'denied' (permanent) | 'prompt'.
 * If permission is granted but device GPS is OFF, fires the one-tap dialog and
 * throws Error('location_disabled') if the Anna declines it.
 */
export const ensureLocationPermission = async ({ request = true } = {}) => {
  if (!isNativeApp()) return 'granted';

  const status = await checkNativeLocationStatus();
  let state = status.permission;

  if (state === 'granted' || !request) {
    if (state === 'granted' && request && !status.locationServicesEnabled) {
      const enabled = await promptEnableDeviceLocation();
      if (!enabled) throw new Error('location_disabled');
    }
    return state;
  }

  if (state === 'prompt') {
    state = await requestNativeLocationPermission();
    if (state === 'granted' && !status.locationServicesEnabled) {
      const enabled = await promptEnableDeviceLocation();
      if (!enabled) throw new Error('location_disabled');
    }
    return state;
  }

  return 'denied';
};

const label = (t, key, fallback) => {
  try {
    const value = t?.(key);
    return value && value !== key ? value : fallback;
  } catch {
    return fallback;
  }
};

const actionButton = (onClick, text) => React.createElement(
  'button',
  {
    type: 'button',
    onClick,
    style: {
      alignSelf: 'flex-start',
      padding: '6px 14px',
      borderRadius: 10,
      border: 'none',
      background: '#007a4d',
      color: '#fff',
      fontWeight: 700,
      fontSize: 13,
    },
  },
  text,
);

/** Location permanently denied — the only way back is the OS Settings screen. */
export const showLocationDeniedToast = (t) => {
  const message = label(
    t,
    'dash.geoDenied',
    'Location is turned off for MyTownly Anna. Turn it on in Settings to go online.',
  );
  if (!isNativeApp()) {
    toast.error(message, { id: 'geo-denied', duration: 6000 });
    return;
  }
  toast.error(
    React.createElement(
      'span',
      { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
      message,
      actionButton(() => { toast.dismiss(); openAppSettings(); }, label(t, 'dash.openSettings', 'Open Settings')),
    ),
    { id: 'geo-denied', duration: 9000 },
  );
};

/** Device GPS hardware is off — offer the one-tap "turn on location" dialog. */
export const showLocationDisabledToast = (t, onRetry) => {
  const message = label(
    t,
    'dash.locationServicesOff',
    'Device location is turned off. Turn on location so passengers can find you.',
  );
  if (!isNativeApp()) {
    toast.error(message, { id: 'geo-disabled', duration: 6000 });
    return;
  }
  toast.error(
    React.createElement(
      'span',
      { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
      message,
      actionButton(async () => {
        toast.dismiss();
        const enabled = await promptEnableDeviceLocation();
        if (enabled && typeof onRetry === 'function') onRetry();
      }, label(t, 'dash.turnOnLocation', 'Turn On Location')),
    ),
    { id: 'geo-disabled', duration: 9000 },
  );
};
